import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, MouseEvent, Ref } from "react";

import { cn } from "@/lib/cn";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring " +
    "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        outline:
          "border border-border bg-card text-foreground hover:bg-secondary",
        ghost: "text-foreground hover:bg-secondary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link: "text-primary underline underline-offset-4 hover:no-underline",
      },
      size: {
        sm: "h-8 px-3",
        md: "h-9 px-4",
        lg: "h-10 px-5",
        icon: "h-9 w-9 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * The rendered element, for the one thing a caller cannot do without it:
   * MOVE FOCUS.
   *
   * `ButtonHTMLAttributes` carries no `ref`, so before this a surface that had
   * to put focus somewhere after a re-render had no handle on a Button at all.
   * React 19 passes `ref` to a function component as an ordinary prop, and it
   * reaches the DOM element through the spread below unchanged; declaring it
   * here is what makes that visible to the type system rather than accidental.
   *
   * Deliberately NOT an escape hatch for imperative styling or measurement:
   * `PathwayStepList` uses it to restore focus after an ordering move, which is
   * a keyboard requirement no declarative arrangement can express.
   */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Disabled buttons stay in the tab order.
 *
 * Every disabled control in this application carries a reason — a missing
 * capability, read-only base content, a locked safety floor — and the native
 * `disabled` attribute removes the element from the tab order and suppresses
 * its pointer events, which hides that reason from exactly the users who most
 * need it. So `disabled` is expressed as `aria-disabled`, the element stays
 * focusable and hoverable, and activation is blocked here instead.
 */
export function Button({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : "button";
  const isDisabled = disabled === true;

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  }

  return (
    <Component
      className={cn(buttonVariants({ variant, size }), className)}
      aria-disabled={isDisabled || undefined}
      onClick={handleClick}
      {...(asChild ? {} : { type })}
      {...props}
    />
  );
}
